/**
 * Tutor edit form.
 * Loads full state, hydrates the form, then PATCHes profile and criminal-record decision in one submit.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGetTutorRemoteFullState, useUpdateTutorRemoteProfile } from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { REMOTE_SUBJECT_OPTIONS } from '@/components/tutors/tutor-configs';
import { useContactExistsCheck, resolveContactCheckType } from '@/hooks/useContactExistsCheck';
import { tutorRemoteEditValidationSchema } from '@/utils/validation-schemas-utils';
import {
  areContactsIdentical,
  isPrimaryContactDirty,
  isPrimaryContactFormValid,
  isPrimaryContactSubmitBlocked,
  readPhoneSetupLink,
  toPrimaryContactMode,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { ContactChangeModal } from '@/components/common/modals/ContactChangeModal';
import { useContactChangeOnSubmit } from '@/hooks/useContactChangeOnSubmit';
import { getLevels } from '@/services/levels.service';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import {
  applyCriminalRecordOnUpdate,
  applyRemoteTutorFieldUpdate,
  buildEditFormDataFromFullState,
  buildUpdateTutorDto,
  setNestedPath,
  defaultUpload,
  mergePendingUploads,
} from './tutor-form-utils';
import { toast } from '@/components/ui/toast';
import { buildPendingDocumentFilePatch } from '@/utils/pending-document-link-utils';
import { hasPendingUploadFiles, uploadPendingFiles } from '@/utils/pending-upload-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { preserveClearedMeetingSessionId } from '@/utils/meeting-selector-utils';
import { TutorPersonalAccountSection } from './tutor-form-builder/TutorPersonalAccountSection';
import { TutorOfficialDocumentsSection } from './tutor-form-builder/TutorOfficialDocumentsSection';
import { TutorSupportSection } from './tutor-form-builder/TutorSupportSection';
import { TutorInformationMeetingSection } from './tutor-form-builder/TutorInformationMeetingSection';
import { TutorCertificationSection } from './tutor-form-builder/TutorCertificationSection';
import { TutorContractSection } from './tutor-form-builder/TutorContractSection';

/** User id and close / success callbacks for the remote tutor edit panel. */
interface EditTutorViewProps {
  userId: string;
  stepKey?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Tutor edit form. */
export function EditTutorView({
  userId,
  stepKey: _stepKey,
  onClose,
  onSuccess,
}: Readonly<EditTutorViewProps>) {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const defaultCountry = useDefaultPhoneCountry();
  const validation = useFormValidation(tutorRemoteEditValidationSchema);

  const { data: levels = [] } = useQuery({
    queryKey: ['levels', 'all'],
    queryFn: async () => (await getLevels({ pageSize: 10 })).data,
  });

  const { data: programsResponse } = useProgramsForMode();
  const programs = programsResponse?.data ?? [];

  const subjectOptions = REMOTE_SUBJECT_OPTIONS;

  const levelOptions = React.useMemo(
    () => levels.map((l: any) => ({ value: l.id, label: l.label })),
    [levels],
  );

  const {
    data: fullState,
    isLoading: isLoadingFullState,
    isError: isFullStateError,
    error: fullStateError,
  } = useGetTutorRemoteFullState(userId, companyId, { enabled: !!userId && !!companyId });
  useUnitGetNotFound({
    isError: isFullStateError,
    error: fullStateError,
    catalogKey: 'tutorNotFound',
    onClose,
    entityId: userId,
  });

  const record = fullState?.record;
  const isPreRegistered = record?.user?.status !== 'active';

  const [formData, setFormData] = React.useState<Record<string, any>>({});
  const [isHydrated, setIsHydrated] = React.useState(false);
  const [pendingFiles, setPendingFiles] = React.useState<Record<string, File | null>>({});
  const [isUploading, setIsUploading] = React.useState(false);

  const primaryContactValue = formData.contact || '';
  const secondaryContactValue = formData.secondaryContact || '';
  const secondaryContactType: 'email' | 'phone' =
    formData.contactType === 'email' ? 'phone' : 'email';

  const { isChecking: isPrimaryChecking, exists: primaryContactExists } = useContactExistsCheck({
    value: primaryContactValue,
    type: resolveContactCheckType(formData.contactType || 'email'),
    excludeUserId: userId,
    skipHydratedValue: true,
  });

  const { isChecking: isSecondaryChecking, exists: contactExists } = useContactExistsCheck({
    value: secondaryContactValue,
    type: resolveContactCheckType(secondaryContactType),
    excludeUserId: userId,
    skipHydratedValue: true,
  });

  const contactsAreIdentical = areContactsIdentical(primaryContactValue, secondaryContactValue);

  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  /** Hydrate form from full-state once it arrives. Keep the spinner until then. */
  React.useEffect(() => {
    if (!fullState?.record) {
      setIsHydrated(false);
      return;
    }
    const nextFormData = preserveClearedMeetingSessionId(
      formDataRef.current,
      buildEditFormDataFromFullState(fullState.record, defaultCountry),
    );
    setFormData(nextFormData);
    validation.validateInitialFormData(nextFormData);
    setIsHydrated(true);
  }, [fullState?.record, defaultCountry]);

  const updateMutation = useUpdateTutorRemoteProfile();
  const contactKind = toPrimaryContactMode(formData.contactType);
  const isPrimaryDirty = isPrimaryContactDirty({
    mode: contactKind,
    currentValue: primaryContactValue,
    storedEmail: record?.user?.email,
    storedPhone: record?.user?.phone,
    serverKind: record?.user?.primaryContactKind,
    defaultCountry,
  });

  /** Close the edit surface after a credential modal or a clean PATCH. */
  function handleContactChangeFinished(): void {
    onSuccess?.();
  }

  const contactChange = useContactChangeOnSubmit({
    isDirty: isPrimaryDirty,
    kind: contactKind,
    ownProfile: false,
    closeForm: handleContactChangeFinished,
  });

  const isSubmitValid = isPrimaryContactFormValid({
    primaryExists: primaryContactExists,
    isPrimaryChecking,
    isSecondaryChecking,
    identical: contactsAreIdentical,
    formValid: validation.isFormValid(),
  });

  /** Set one field (supports dotted paths). Changing contact type drops the secondary contact. */
  const handleChange = (key: string, value: any) => {
    setFormData((prev) => {
      const next = applyRemoteTutorFieldUpdate(prev, key, value, defaultCountry);
      const updatedData = { ...next, _contactType: next.contactType };
      validation.validateField(key, value, updatedData);
      return next;
    });
    validation.markAsTouched(key);
    validation.markAsDirty(key);
  };

  /** Patch several fields at once (supports dotted paths). Validates after the state flush. */
  const handleBatchChange = (patch: Record<string, any>) => {
    setFormData((prev) => {
      let next = { ...prev };
      Object.entries(patch).forEach(([key, value]) => {
        if (key.includes('.')) {
          next = setNestedPath(next, key, value);
        } else {
          next[key] = value;
        }
      });
      formDataRef.current = next;
      return next;
    });
    // Defer so setFormData settles before validation.
    setTimeout(() => {
      const updatedData = { ...formDataRef.current, _contactType: formDataRef.current.contactType };
      Object.entries(patch).forEach(([key, value]) => {
        validation.validateField(key, value, updatedData);
        validation.markAsTouched(key);
        validation.markAsDirty(key);
      });
    }, 0);
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Store the file for submit upload and write a pending link so required docs validate. */
  const handleFileChange = (key: string, file: File | null) => {
    setPendingFiles((prev) => ({ ...prev, [key]: file }));
    const patch = buildPendingDocumentFilePatch(key, file);
    if (patch) handleBatchChange(patch);
  };

  /** Validate, upload pending files, PATCH profile, then sync criminal-record decision if the checkbox changed. */
  const handleSubmit = async () => {
    if (
      isPrimaryContactSubmitBlocked(primaryContactExists, isPrimaryChecking, contactsAreIdentical)
    )
      return;
    const isValid = validation.validateForm({
      ...formData,
      _contactType: formData.contactType,
      _contactExists: primaryContactExists ?? contactExists,
    });
    if (!isValid) return;

    if (hasPendingUploadFiles(pendingFiles)) {
      setIsUploading(true);
    }
    let uploadedUrls: Record<string, string> = {};
    try {
      uploadedUrls = await uploadPendingFiles(pendingFiles, defaultUpload);
    } catch (err) {
      toast.error(`Erreur lors de l'upload : ${getErrorMessage(err)}`);
      setIsUploading(false);
      return;
    }

    const enrichedData = mergePendingUploads(formData, uploadedUrls, false);

    if (!record) return;
    const { dto, wasValidated, nowValidated, tutorUserId } = buildUpdateTutorDto({
      formData: enrichedData,
      fullState,
      record,
      defaultCountry,
      isPreRegistered,
    });

    contactChange.requestSubmit(() => {
      updateMutation.mutate(
        { userId, dto, companyId },
        {
          onSuccess: async (data) => {
            if (tutorUserId) {
              try {
                await applyCriminalRecordOnUpdate(tutorUserId, wasValidated, nowValidated);
              } catch (err) {
                console.warn('[EditTutorView] Criminal record decision failed:', err);
              }
            }
            if (contactChange.isIntercepting) {
              contactChange.onMutationSettled({ ok: true, link: readPhoneSetupLink(data) });
              return;
            }
            toast.success('Dossier mis à jour avec succès');
            handleContactChangeFinished();
          },
          onError: (err: unknown) => {
            toast.error(getErrorMessage(err));
            contactChange.onMutationSettled({ ok: false });
          },
        },
      );
    });
    setIsUploading(false);
  };

  /** True until GET tutor hydrates the form. */
  const isFormLoading = isLoadingFullState || !record || !isHydrated;

  function renderFormSections(): React.ReactNode {
    if (!record || !isHydrated) {
      return null;
    }
    return (
      <>
        <TutorPersonalAccountSection
          formData={formData}
          onChange={handleChange}
          onBlur={handleBlur}
          validation={validation}
          mode="edit"
          defaultCountry={defaultCountry}
          userId={userId}
          emailVerified={record.user?.emailVerified}
          contactExists={contactExists}
          primaryContactExists={primaryContactExists}
          contactsAreIdentical={contactsAreIdentical}
          serverEmail={record.user?.email}
          serverPhone={record.user?.phone}
          serverPrimaryKind={record.user?.primaryContactKind}
        />
        <TutorOfficialDocumentsSection
          formData={formData}
          onChange={handleChange}
          onBatchChange={handleBatchChange}
          tutorUserId={userId}
        />
        <TutorSupportSection
          formData={formData}
          onChange={handleChange}
          programs={programs}
          levelOptions={levelOptions}
          subjectOptions={subjectOptions}
          mode="edit"
        />
        <TutorInformationMeetingSection
          formData={formData}
          onChange={handleChange}
          userId={userId}
          companyId={companyId}
          mode="edit"
          user={user}
        />
        <TutorCertificationSection
          formData={formData}
          onChange={handleChange}
          onFileSelect={handleFileChange}
        />
        <TutorContractSection
          formData={formData}
          onChange={handleChange}
          onBatchChange={handleBatchChange}
          onFileSelect={handleFileChange}
        />
      </>
    );
  }

  return (
    <>
      <CollapseReplaceForm
        formType="edit"
        title="Modifier le tuteur"
        subtitle="Modifiez les informations du tuteur"
        onClose={onClose}
        onSubmit={handleSubmit}
        submitLabel="Enregistrer"
        isSubmitting={isUploading || updateMutation.isPending || contactChange.modal.open}
        isValid={isSubmitValid}
        isLoading={isFormLoading}
      >
        {renderFormSections()}
      </CollapseReplaceForm>
      <ContactChangeModal
        open={contactChange.modal.open}
        kind={contactChange.modal.kind}
        ownProfile={contactChange.modal.ownProfile}
        phase={contactChange.modal.phase}
        phoneSetupLink={contactChange.modal.phoneSetupLink}
        onClose={contactChange.cancel}
        onConfirm={contactChange.confirm}
        onCopyLink={contactChange.onCopyLink}
      />
    </>
  );
}
